import { authedFetch, UnauthorizedError } from '@apis/auth';

export interface IAdminManager {
  id: string;
  name: string;
  tradeId?: string;
  tradeGroup?: string;
  backgroundColor?: string;
  sortIndex?: number;
  phoneNumber: string | null;
  receiveNotifications: boolean;
}

/** The role dropdown's catalog entry (rich Trusstor trade shape). */
export interface IManagementTrade {
  tradeId: string;
  tradeGroup: string;
  backgroundColor: string;
  sortIndex: number;
  tradeTranslations?: Record<string, string>;
}

export interface ICreateManagerInput {
  tradeId: string;
  name: string;
  phone: string | null;
}

/** Backend answered 409 DUPLICATE_MANAGER — an identical manager exists. */
export class DuplicateManagerError extends Error {
  constructor() {
    super('this manager already exists in the system');
    this.name = 'DuplicateManagerError';
  }
}

/** Deletion refused — the manager still has a tag or activities assigned. */
export class ManagerInUseError extends Error {
  constructor(public readonly reason: 'tags' | 'activities') {
    super(`manager has ${reason} assigned`);
    this.name = 'ManagerInUseError';
  }
}

async function parseErrors(res: Response): Promise<never> {
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  const error = body?.error as Record<string, unknown> | undefined;
  const code = error?.code as string | undefined;
  if (res.status === 401) throw new UnauthorizedError();
  if (res.status === 409 && code === 'DUPLICATE_MANAGER') throw new DuplicateManagerError();
  if (res.status === 409 && code === 'MANAGER_IN_USE') {
    const reason = (error?.details as { reason?: string } | undefined)?.reason;
    throw new ManagerInUseError(reason === 'activities' ? 'activities' : 'tags');
  }
  throw new Error(`request failed: ${res.status} ${(error?.message as string | undefined) ?? ''}`);
}

export async function fetchAdminManagers(
  siteId: string,
  signal?: AbortSignal,
): Promise<{ managers: IAdminManager[] }> {
  const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/admin/managers`, {
    signal,
  });
  if (res.ok) return (await res.json()) as { managers: IAdminManager[] };
  return parseErrors(res);
}

export async function fetchManagementTrades(
  siteId: string,
  signal?: AbortSignal,
): Promise<{ trades: IManagementTrade[] }> {
  const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/admin/managers/trades`, {
    signal,
  });
  if (res.ok) return (await res.json()) as { trades: IManagementTrade[] };
  return parseErrors(res);
}

export async function createAdminManager(
  siteId: string,
  input: ICreateManagerInput,
): Promise<{ manager: IAdminManager }> {
  const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/admin/managers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (res.ok) return (await res.json()) as { manager: IAdminManager };
  return parseErrors(res);
}

export async function updateAdminManager(
  siteId: string,
  managerId: string,
  input: { phone: string | null },
): Promise<{ manager: IAdminManager }> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/admin/managers/${encodeURIComponent(managerId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  );
  if (res.ok) return (await res.json()) as { manager: IAdminManager };
  return parseErrors(res);
}

export async function deleteAdminManager(siteId: string, managerId: string): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/admin/managers/${encodeURIComponent(managerId)}`,
    { method: 'DELETE' },
  );
  if (res.ok) return;
  return parseErrors(res);
}
